import queryString from 'query-string';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';

import { selectCurrency, selectLocale } from '~/components/account/ducks/index';
import BinaryQuestion from '~/components/binary-question';
import { gettoolbarLeftMargin } from '~/components/casino-shared/game-loader/ducks/toolbar-utils';
import { showNotification } from '~/components/notifications/ducks/notifications';
import KYC from '~/components/payment/deposit/components/kyc';
import {
	ROUTE_ACCOUNT_DEPOSIT,
	ROUTE_ACCOUNT_MY_LIMITS,
	ROUTE_ACCOUNT_SOURCE_OF_WEALTH,
	ROUTE_MOBILE_QUICK_DEPOSIT
} from '~/components/router/constants';
import useTranslations from '~/components/translation/use-translations.ts';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import useScreenOrientation from '~/hooks/use-orientation';
import currencyFormatter from '~/libs/currency-formatter';
import { getorientation } from '~/utils/style-utils.ts';
import usePaymentTracking from '~/components/payment-beta/deposit/hooks/use-payment-tracking';

import ExecuteHandler from './components/execute-handler';
import ResultNotification from './components/result-notification';
import MainContent from './quick-deposit-mobile-content';
import NewQDMainContent from '~/components/payment/quick-deposit-beta/components-v2';
import {
	DEPOSIT_REDIRECT_CONFIRM,
	LIMITS_REDIRECT_CONFIRM,
	LOADING,
	MAIN_CONTENT,
	QD_EXECUTE,
	QD_REDIRECT_URL,
	RESULT_NOTIFICATION,
	RESULT_VIEW,
	UPLOAD_DOC_REDIRECT_CONFIRM,
	textKeysQD as textKeys
} from './utils/constants.js';
import { Spinner } from './utils/qd-styled-utils';
import reducers, { addView, hideView } from './utils/reducers';
import { selectEnableNewCasinoQDDesigns } from '~/store/feature-toggles/payments-feature-toggles';

const MainWrapper = styled.div`
	height: 100%;

	${getorientation.landscape`
			 .binary-question__inner {
					margin: auto;
					width: 270px;
				}
	`}
`;

const QuickDepositMobile = ({ fullLocation }) => {
	const location = useLocation();
	const router = useRouter();
	const dispatch = useDispatch();
	const [kyc, setKyc] = useState({});
	const QDRedirectUrl = JSON.parse(window.localStorage.getItem(QD_REDIRECT_URL));
	const showNewCasinoQDDesigns = useSelector(selectEnableNewCasinoQDDesigns);
	const locale = useSelector(selectLocale);
	const currency = useSelector(selectCurrency);
	const screenOrientation = useScreenOrientation();
	const closeCurrentContext = useCloseCurrentContext();
	const transformPath = useContextualPathTransformer();
	const [views, updateViews] = useReducer(reducers, [MAIN_CONTENT]);
	const { isLoadingTranslations, translations: t } = useTranslations(textKeys);
	const { trackQDEvent } = usePaymentTracking();

	const showResult = fullLocation.state?.referrer === RESULT_VIEW;
	const depositedAmount = showResult && fullLocation.state?.amount;
	const queryParams = queryString.parse(location.search);
	const [resultParams, setResultParams] = useState(null);
	const [executeResult, updateExecuteResult] = useState(null);
	const routeState = {
		referrer: ROUTE_MOBILE_QUICK_DEPOSIT,
		redirectUrl: fullLocation
	};

	const showNewQDFlow = useMemo(() => showNewCasinoQDDesigns, [showNewCasinoQDDesigns]);

	const handleCancelLeaveGamePopup = viewToHide => {
		// Track cancel action
		trackQDEvent({
			pixel_deposit_action: 'click-cancel-leave-game-popup'
		});
		updateViews(hideView(viewToHide));
	};

	const redirectToPayments = () => {
		// Track redirect to standard cashier
		trackQDEvent({
			pixel_deposit_action: 'click-choose-another-method'
		});

		closeCurrentContext();
		// TODO (navigation) - use search params or redux for state
		router.push(transformPath(ROUTE_ACCOUNT_DEPOSIT), { state: routeState });
	};

	const redirectToLimits = () => {
		// Track redirect to limits page
		trackQDEvent({
			pixel_deposit_action: 'click-accept-leave-game-popup'
		});

		closeCurrentContext();
		// TODO (navigation) - use search params or redux for state
		router.push(transformPath(ROUTE_ACCOUNT_MY_LIMITS, { state: routeState }));
	};

	const redirectToSow = () => {
		closeCurrentContext();

		dispatch(
			showNotification({
				message: t.DEPOSIT_AML_SOW_LIMIT_INFO,
				color: 'neutral',
				autoHide: true
			})
		);

		// TODO (navigation) - use search params or redux for state
		router.push(transformPath(ROUTE_ACCOUNT_SOURCE_OF_WEALTH, { state: routeState }));
	};

	useEffect(() => {
		if (!resultParams && queryParams?.accHistRef) {
			setResultParams(queryParams);
		}
	}, [queryParams, resultParams]);

	useEffect(() => {
		// 'EXTERNAL_WINDOW' redirection flow.
		if (QDRedirectUrl) {
			window.localStorage.removeItem(QD_REDIRECT_URL);
			router.push(`${QDRedirectUrl.pathname}${fullLocation.search}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (resultParams?.accHistRef && !views.includes(RESULT_NOTIFICATION)) {
			updateViews(addView(RESULT_NOTIFICATION, LOADING));
			updateViews(hideView(MAIN_CONTENT));
		}
	}, [resultParams, views]);

	const MainContentComponent = useMemo(() => (showNewQDFlow ? NewQDMainContent : MainContent), [showNewQDFlow]);

	useEffect(() => {
		if (showResult && !isLoadingTranslations) {
			if (depositedAmount) {
				// Track successful deposit result
				trackQDEvent({
					pixel_deposit_action: 'quick-deposit-success-result',
					pixel_depositAmount: depositedAmount
				});

				const formattedAmount = currencyFormatter.format(depositedAmount, currency, locale);
				dispatch(
					showNotification({
						message: `${formattedAmount} ${t.QUICK_DEPOSIT_SUCCESS}`,
						color: 'green',
						autoHide: true,
						customMargin: gettoolbarLeftMargin(screenOrientation)
					})
				);
			}
			closeCurrentContext();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, isLoadingTranslations]);

	if (showResult) {
		return null;
	}

	const components = {
		[MAIN_CONTENT]: (
			<MainContentComponent
				key={MAIN_CONTENT}
				views={views}
				setKyc={setKyc}
				addView={addView}
				hideView={hideView}
				updateViews={updateViews}
				updateExecuteResult={updateExecuteResult}
				isLandscape={screenOrientation}
				redirectToPayments={redirectToPayments}
				redirectToSow={redirectToSow}
			/>
		),
		[RESULT_NOTIFICATION]: (
			<ResultNotification
				key={RESULT_NOTIFICATION}
				resultParams={resultParams}
				fullLocation={fullLocation}
				isLandscape={screenOrientation}
			/>
		),
		[LIMITS_REDIRECT_CONFIRM]: (
			<BinaryQuestion
				key={LIMITS_REDIRECT_CONFIRM}
				shouldOpenBinary
				renderInParent
				className="quick-deposit"
				title={t.INSTANT_DEPOSIT_REDIRECT_CONFIRM_TITLE}
				text={t.INSTANT_DEPOSIT_LIMIT_REDIRECT_CONFIRM_TEXT}
				yesText={t.INSTANT_DEPOSIT_ACCEPT}
				onYesClick={redirectToLimits}
				noText={t.CANCEL}
				onNoClick={() => handleCancelLeaveGamePopup(LIMITS_REDIRECT_CONFIRM)}
				onOutsideClick={() => handleCancelLeaveGamePopup(LIMITS_REDIRECT_CONFIRM)}
			/>
		),
		[UPLOAD_DOC_REDIRECT_CONFIRM]: (
			<KYC key={UPLOAD_DOC_REDIRECT_CONFIRM} uploadDocuments={kyc.uploadDocuments} routeState={routeState} />
		),
		[DEPOSIT_REDIRECT_CONFIRM]: (
			<BinaryQuestion
				key={DEPOSIT_REDIRECT_CONFIRM}
				shouldOpenBinary
				renderInParent
				className="quick-deposit"
				title={t.INSTANT_DEPOSIT_REDIRECT_CONFIRM_TITLE}
				text={t.INSTANT_DEPOSIT_REDIRECT_CONFIRM_TEXT}
				yesText={t.INSTANT_DEPOSIT_ACCEPT}
				onYesClick={() => {
					// Track accept action
					trackQDEvent({
						pixel_deposit_action: 'click-accept-leave-game-popup'
					});
					redirectToPayments();
				}}
				noText={t.CANCEL}
				onNoClick={() => handleCancelLeaveGamePopup(DEPOSIT_REDIRECT_CONFIRM)}
				onOutsideClick={() => handleCancelLeaveGamePopup(DEPOSIT_REDIRECT_CONFIRM)}
			/>
		),

		[QD_EXECUTE]: (
			<ExecuteHandler
				key={QD_EXECUTE}
				result={executeResult}
				updateViews={updateViews}
				hideView={hideView}
				addView={addView}
				setResultParams={setResultParams}
				fullLocation={fullLocation}
			/>
		),
		[LOADING]: !showNewQDFlow && <Spinner key={LOADING} className="spinner" />
	};

	return <MainWrapper data-at="qd-main-wrapper">{views.map(component => components[component])}</MainWrapper>;
};

export default QuickDepositMobile;
